import * as React from 'react'

import {
  APPOINTMENTS_PAGE_LIMIT_MAX,
  VISITS_PAGE_LIMIT_MAX,
} from '@/config/constants'
import type { AppointmentRecord } from '@/features/appointments/types'
import { useCapability } from '@/features/permissions/hooks'
import type { VisitRecord } from '@/features/visits/types'
import { useAppointmentsRangeQuery } from '@/hooks/useAppointmentsQuery'
import { useProviderOptions } from '@/hooks/useProviderOptions'
import { useVisitsRangeQuery } from '@/hooks/useVisitsQuery'

/** One slice of history: absent when the viewer may not see it. */
export type HistorySlice<T> =
  | { allowed: false }
  | {
      allowed: true
      isLoading: boolean
      isError: boolean
      rows: T[]
      /** The page cap stopped the fetch; counts are a floor. */
      truncated: boolean
    }

export interface ClientHistory {
  visits: HistorySlice<VisitRecord>
  appointments: HistorySlice<AppointmentRecord>
  /** provider id → name, for resolving visit providers. */
  providerNames: ReadonlyMap<string, string>
  /**
   * Whether provider names can be resolved. Visits carry only provider ids, and
   * the provider list sits behind its own capability.
   */
  providers: { allowed: boolean; isLoading: boolean; isError: boolean }
}

export const NO_ROWS: never[] = []

function patientFilter(patientId: string) {
  return [
    {
      id: 'patient_id',
      field: 'patient_id',
      operator: '=' as const,
      value: patientId,
    },
  ]
}

/**
 * A query as a slice. Memoized on the query's own fields, so the slice and its
 * rows keep their identity between renders and memos keyed on them hold.
 */
export function useSlice<T>(
  allowed: boolean,
  query: { isLoading: boolean; isError: boolean },
  rows: T[] | undefined,
  truncated: boolean | undefined
): HistorySlice<T> {
  const { isLoading, isError } = query
  return React.useMemo(
    () =>
      allowed
        ? {
            allowed: true,
            isLoading,
            isError,
            rows: rows ?? NO_ROWS,
            truncated: truncated ?? false,
          }
        : { allowed: false },
    [allowed, isLoading, isError, rows, truncated]
  )
}

/**
 * Visit and appointment history for one client, each gated on the viewer's
 * capability for that data so a role without it sees nothing rather than an
 * error. Both lists are read whole (every page) because the sheet shows
 * counts and builds a care team from them.
 */
export function useClientHistory(patientId: string | undefined): ClientHistory {
  const canVisits = useCapability('data.visits.view') === true
  const canAppointments = useCapability('data.appointments.view') === true
  const canProviders = useCapability('data.providers.view') === true

  const visitsQuery = useVisitsRangeQuery(
    {
      limit: VISITS_PAGE_LIMIT_MAX,
      sort_by: 'visit_date',
      sort_order: 'desc',
      filters: patientId ? patientFilter(patientId) : [],
    },
    { enabled: canVisits && !!patientId }
  )
  const appointmentsQuery = useAppointmentsRangeQuery(
    {
      limit: APPOINTMENTS_PAGE_LIMIT_MAX,
      sort_by: 'source_appointment_date',
      sort_order: 'desc',
      filters: patientId ? patientFilter(patientId) : [],
    },
    { enabled: canAppointments && !!patientId }
  )
  // Names only matter for the care team, which is built from visits.
  const canNameProviders = canVisits && canProviders
  const providersQuery = useProviderOptions(canNameProviders)

  const providerNames = React.useMemo(
    () =>
      new Map(
        (providersQuery.data ?? []).map((provider) => [
          provider.id,
          provider.name,
        ])
      ),
    [providersQuery.data]
  )

  const visits = useSlice(
    canVisits,
    visitsQuery,
    visitsQuery.data?.visits,
    visitsQuery.data?.truncated
  )
  const appointments = useSlice(
    canAppointments,
    appointmentsQuery,
    appointmentsQuery.data?.appointments,
    appointmentsQuery.data?.truncated
  )
  const providers = React.useMemo(
    () => ({
      allowed: canNameProviders,
      isLoading: canNameProviders && providersQuery.isLoading,
      isError: providersQuery.isError,
    }),
    [canNameProviders, providersQuery.isLoading, providersQuery.isError]
  )

  return React.useMemo(
    () => ({ visits, appointments, providerNames, providers }),
    [visits, appointments, providerNames, providers]
  )
}
